import * as React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, CircleAlert, GitCompare, Loader } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import { cn } from '@/utils'
import { EDocumentType } from '@/utils/constants'

import { EDocumentComparisonStatus } from '../../constants'
import { useAllVersionsReady, useGetFileContentByBlobURL } from '../../hooks'
import { IHTMLParserResult } from '../../html/types'
import { diffHTMLDocuments } from '../../html/utils'
import { IPDFParserResult } from '../../pdf/types'
import { diffPDFDocuments } from '../../pdf/utils'
import { useVersionComparisonStore } from '../../store'

import { ComparisonLayout } from './Layout'
import { ComparisonMinimap } from './Minimap'
import { ComparisonSidebar } from './Sidebar'

/**
 * Main comparison layout dialog component
 * Displays two document versions side by side with a minimap in the middle
 * and a sidebar on the right showing changes
 */
const ComparisonLayoutDialog: React.FC = () => {
  const { t } = useTranslation()
  const {
    documentId,
    documentFilename,
    documentType,
    compareVersions,
    compareDocumentStatus,
    setCompareResult,
    reset,
  } = useVersionComparisonStore()
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false)

  // Check if all versions are ready (required for zoom and scroll features)
  const allVersionsReady = useAllVersionsReady()

  const sourceVersion = React.useMemo(() => {
    return compareVersions?.find((version) => version.isSource) ?? null
  }, [compareVersions])

  const targetVersion = React.useMemo(() => {
    return compareVersions?.find((version) => version.isTarget) ?? null
  }, [compareVersions])

  const isOpen = React.useMemo(() => {
    return !!documentId && !!compareVersions && compareVersions.length > 0
  }, [documentId, compareVersions])

  const { data: sourceContent } = useGetFileContentByBlobURL({
    signedUrl: sourceVersion?.parserUrl ?? '',
    documentId: documentId ?? '',
    versionId: sourceVersion?.id ?? '',
  })

  /**
   * Parse source version content to object
   */
  const sourceData = React.useMemo<
    IHTMLParserResult | IPDFParserResult | null
  >(() => {
    return sourceContent ? JSON.parse(sourceContent) : null
  }, [sourceContent])

  const { data: targetContent } = useGetFileContentByBlobURL({
    signedUrl: targetVersion?.parserUrl ?? '',
    documentId: documentId ?? '',
    versionId: targetVersion?.id ?? '',
  })

  /**
   * Parse target version content to object
   */
  const targetData = React.useMemo<
    IHTMLParserResult | IPDFParserResult | null
  >(() => {
    return targetContent ? JSON.parse(targetContent) : null
  }, [targetContent])

  /**
   * Compare source and target versions
   * This effect runs when:
   * - all versions are ready
   * - source data is available
   * - target data is available
   */
  React.useEffect(() => {
    // Compare source and target versions when all versions are ready and source/target data is available
    if (!allVersionsReady || !sourceData || !targetData) return

    // Compare HTML versions with word-level diff
    if (documentType === EDocumentType.HTML) {
      const htmlComparisonResult = diffHTMLDocuments(
        sourceData as IHTMLParserResult,
        targetData as IHTMLParserResult
      )
      setCompareResult({
        diff: htmlComparisonResult,
        status: EDocumentComparisonStatus.COMPARED,
      })
    }

    // Compare PDF versions with word-level diff
    if (documentType === EDocumentType.PDF) {
      const pdfComparisonResult = diffPDFDocuments(
        sourceData as IPDFParserResult,
        targetData as IPDFParserResult
      )
      setCompareResult({
        diff: pdfComparisonResult,
        status: EDocumentComparisonStatus.COMPARED,
      })
    }
  }, [allVersionsReady, documentType, setCompareResult, sourceData, targetData])

  return (
    <Dialog open={isOpen} onOpenChange={() => reset()}>
      <DialogContent className="gap-0 inset-0 !max-w-full !max-h-full translate-x-0 translate-y-0 rounded-none p-0">
        <DialogHeader className="flex-row gap-2 items-center flex-shrink-0 px-3xl py-2xl border-b border-secondary space-y-0">
          <DialogTitle className="m-0 font-semibold text-xl text-primary-900">
            {documentFilename}
          </DialogTitle>
          <div className="inline-flex justify-center items-center gap-xxs border border-brand-utility-brand-200 py-xxs px-md bg-brand-utility-brand-50 rounded-full text-xs font-medium text-brand-utility-brand-700">
            <GitCompare className="text-brand-utility-brand-500 size-3" />
            {t('versionComparison.dialog.compareVersions')}
          </div>
          {(() => {
            let icon = null
            let className = ''
            switch (compareDocumentStatus) {
              case EDocumentComparisonStatus.COMPARING:
                icon = (
                  <>
                    <Loader
                      size={12}
                      className="size-4 text-foreground-fg-warning-secondary"
                    />
                    Comparing
                  </>
                )
                className = 'border-warning-200 bg-warning-50 text-warning-700'
                break
              case EDocumentComparisonStatus.COMPARED:
                icon = (
                  <>
                    <Check className="size-4 text-foreground-fg-success-secondary" />
                    Compared
                  </>
                )
                className = 'border-success-200 bg-success-50 text-success-700'
                break
              case EDocumentComparisonStatus.FAILED:
                icon = (
                  <>
                    <CircleAlert className="size-4 text-foreground-fg-error-secondary" />
                    Failed
                  </>
                )
                className = 'border-error-200 bg-error-50 text-error-700'
                break

              default:
                break
            }

            if (!compareDocumentStatus) return null
            return (
              <div
                className={cn(
                  'flex gap-0.5 items-center px-md py-xxs rounded-sm',
                  'text-xs font-medium',
                  'border border-solid',
                  className
                )}
              >
                {icon}
              </div>
            )
          })()}
        </DialogHeader>

        <div className="flex-1 flex bg-tertiary overflow-hidden">
          {/* Main comparison area: Previous version | Minimap | Current version */}
          <div className="flex-1 px-5 py-4 min-w-0">
            <div className="size-full flex gap-lg p-1 overflow-y-hidden overflow-x-auto">
              {/* Previous version (left side) */}
              {sourceVersion && <ComparisonLayout version={sourceVersion} />}

              {/* Minimap scrollbar (middle) */}
              <ComparisonMinimap />

              {/* Current version (right side) */}
              {targetVersion && <ComparisonLayout version={targetVersion} />}
            </div>
          </div>

          {/* Changes sidebar (right side) */}
          <ComparisonSidebar
            isSidebarCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed((prev) => !prev)}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}

const ComparisonLayoutDialogMemoized = React.memo(ComparisonLayoutDialog)
ComparisonLayoutDialogMemoized.displayName = 'ComparisonLayoutDialog'

export { ComparisonLayoutDialogMemoized as ComparisonLayoutDialog }
